import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import type { SelectProps } from 'antd/es/select';
import { useEffect, useMemo, useRef, useState } from 'react';

interface DebounceSelectProps<ValueType = any>
    // extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> 
    extends SelectProps<ValueType | ValueType[]> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
    initOptions?: boolean
}

export default function DebounceSelect<
    ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any,
>({ fetchOptions, debounceTimeout = 800, initOptions = true, ...props }: DebounceSelectProps<ValueType>) {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const fetchRef = useRef(0);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }

                setOptions(newOptions);
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    useEffect(() => {
        debounceFetcher()
    }, [debounceFetcher])

    return (
        <Select
            // labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin className='center p-3' size="small" /> : null}
            options={options}
            {...props}
        />
    );
}